// src/pages/CustomerRideSearch.js
import React, { useState, useRef, useEffect } from "react";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE;

const CustomerRideSearch = () => {
  const fromRef = useRef(null);
  const toRef = useRef(null);
  const navigate = useNavigate();

  const [date, setDate] = useState("");
  const [seats, setSeats] = useState(1);

  const [recentSearches, setRecentSearches] = useState([]);

  const customerEmail = localStorage.getItem("userEmail");

  useEffect(() => {
    if (!customerEmail) navigate("/login");

    // Load recent searches
    const saved = JSON.parse(localStorage.getItem("recentRideSearches") || "[]");
    setRecentSearches(saved);
  }, [customerEmail, navigate]);

  if (!customerEmail) {
    return <div className="p-6 text-center text-gray-500">Redirecting...</div>;
  }

  const saveSearchToHistory = (search) => {
    let saved = JSON.parse(localStorage.getItem("recentRideSearches") || "[]");

    // Remove duplicates (same from + to + date)
    saved = saved.filter(
      (s) =>
        !(
          s.from === search.from &&
          s.to === search.to &&
          s.date === search.date &&
          s.seats === search.seats
        )
    );

    // Add new search to the top
    saved.unshift(search);

    // Keep max 5 searches
    saved = saved.slice(0, 5);

    localStorage.setItem("recentRideSearches", JSON.stringify(saved));
    setRecentSearches(saved);
  };

  const applySearch = (s) => {
    fromRef.current.value = s.from;
    toRef.current.value = s.to;
    setDate(s.date);
    setSeats(s.seats);
  };

  const handleSearch = async () => {
    const from = fromRef.current?.value || "";
    const to = toRef.current?.value || "";

    if (!from || !to || !date || !seats) {
      alert("Please fill all fields");
      return;
    }

    saveSearchToHistory({ from, to, date, seats });

    try {
      const res = await axios.post(`${API_BASE}/api/ride/search`, {
        from,
        to,
        date,
      });

      navigate("/customer/ride-results", {
        state: { rides: res.data, from, to, date, seats },
      });
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Try again.");
    }
  };

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
      libraries={["places"]}
    >
      <div className="min-h-screen bg-gradient-to-b from-blue-600 via-blue-300 to-white py-10 px-4 flex justify-center font-[Poppins]">
        <div className="max-w-xl w-full">

          {/* HEADER */}
          <div className="text-center mb-10">
            <h1 className="text-5xl font-extrabold text-white drop-shadow-lg">
              Find Your Ride
            </h1>
            <p className="mt-3 text-lg text-white/90">Fast • Safe • Reliable</p>
          </div>

          {/* MAIN CARD */}
          <div className="bg-white/40 backdrop-blur-xl shadow-2xl rounded-3xl p-8 border border-white/20">

            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Search for a Ride
            </h2>

            <div className="space-y-4">

              {/* FIXED AUTOCOMPLETE — DROPDOWN UNDER FIELD */}
              <div>
                <label className="text-gray-700 font-semibold">
                  Pickup Location
                </label>
                <div className="relative z-50">
                  <Autocomplete>
                    <input
                      ref={fromRef}
                      placeholder="Where from?"
                      className="w-full mt-1 h-12 px-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 transition"
                    />
                  </Autocomplete>
                </div>
              </div>

              <div>
                <label className="text-gray-700 font-semibold">
                  Drop-off Location
                </label>
                <div className="relative z-50">
                  <Autocomplete>
                    <input
                      ref={toRef}
                      placeholder="Where to?"
                      className="w-full mt-1 h-12 px-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 transition"
                    />
                  </Autocomplete>
                </div>
              </div>

              <div>
                <label className="text-gray-700 font-semibold">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full mt-1 h-12 px-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 transition"
                />
              </div>

              <div>
                <label className="text-gray-700 font-semibold">Passengers</label>
                <input
                  type="number"
                  min={1}
                  value={seats}
                  onChange={(e) => setSeats(e.target.value)}
                  className="w-full mt-1 h-12 px-4 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 transition"
                />
              </div>
            </div>

            {/* SEARCH BUTTON */}
            <button
              onClick={handleSearch}
              className="w-full mt-6 h-12 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 active:scale-[.98] transition shadow-md"
            >
              🔍 Search Rides
            </button>
          </div>

          {/* RECENT SEARCHES */}
          {recentSearches.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Recent Searches
              </h3>

              <div className="space-y-3">
                {recentSearches.map((s, idx) => (
                  <div
                    key={idx}
                    onClick={() => applySearch(s)}
                    className="p-4 rounded-xl bg-white shadow flex justify-between items-center cursor-pointer hover:bg-blue-50 transition"
                  >
                    <div>
                      <p className="font-semibold text-gray-800">
                        {s.from} → {s.to}
                      </p>
                      <p className="text-sm text-gray-500">
                        {s.date} • {s.seats} seat(s)
                      </p>
                    </div>

                    <span className="text-blue-600 font-bold text-xl">↺</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </LoadScript>
  );
};

export default CustomerRideSearch;
